import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'modules/views/shared/Button';
import { useStyles } from '../styles';
import { RequiredCertificate } from '../types';

interface RequiredCertificateCardProps {
  certificate: RequiredCertificate;
  onAddCertificate: (certificate: RequiredCertificate) => void;
  onViewDetails: (certificate: RequiredCertificate) => void;
}

export const RequiredCertificateCard: React.FC<RequiredCertificateCardProps> = ({
  certificate,
  onAddCertificate,
  onViewDetails,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const getInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  // Group certifications by validation type
  const { allMandatory, atLeastOneMandatory, notMandatory } = useMemo(() => {
    const allMandatoryGroups: string[] = [];
    const atLeastOneMandatoryGroups: string[] = [];
    const notMandatoryGroups: string[] = [];

    certificate.certificationGroups.forEach(group => {
      if (group.validationType === 3) {
        // All mandatory
        allMandatoryGroups.push(group.name);
      } else if (group.validationType === 2) {
        // At least one mandatory
        atLeastOneMandatoryGroups.push(group.name);
      } else if (group.validationType === 1) {
        // Not mandatory (optional)
        notMandatoryGroups.push(group.name);
      }
    });

    return {
      allMandatory: allMandatoryGroups,
      atLeastOneMandatory: atLeastOneMandatoryGroups,
      notMandatory: notMandatoryGroups,
    };
  }, [certificate.certificationGroups]);

  return (
    <div className={classes.requiredCertificateCard} data-testid={`required-certificate-card-${certificate.id}`}>
      {/* Header with Project Initial and Name */}
      <div className={classes.projectHeader}>
        <div className={classes.projectAvatar}>{getInitial(certificate.projectName)}</div>
        <h3 className={classes.projectTitle} data-testid={`required-certificate-card-name-${certificate.id}`}>
          {certificate.projectName}
        </h3>
      </div>

      {/* All Mandatory Section */}
      <div className={classes.certificationGroup}>
        <p className={classes.groupLabel}>{t('myCertifications.card.allMandatory')}</p>
        <p className={classes.groupContent}>{allMandatory.length > 0 ? allMandatory.join(', ') : '--'}</p>
      </div>

      {/* At Least One Mandatory Section */}
      <div className={classes.certificationGroup}>
        <p className={classes.groupLabel}>{t('myCertifications.card.atLeastOneMandatory')}</p>
        <p className={classes.groupContent}>{atLeastOneMandatory.length > 0 ? atLeastOneMandatory.join(', ') : '--'}</p>
      </div>

      {/* Not Mandatory Section */}
      <div className={classes.certificationGroup}>
        <p className={classes.groupLabel}>{t('myCertifications.card.notMandatory')}</p>
        <p className={classes.groupContent}>{notMandatory.length > 0 ? notMandatory.join(', ') : '--'}</p>
      </div>

      {/* Action Buttons */}
      <div className={classes.cardActions}>
        <Button
          label={t('myCertifications.card.viewDetails')}
          variant="outlined"
          color="primary"
          disableRipple
          styleClass={classes.requiredButton}
          testId={`required-certificate-view-details-button-${certificate.id}`}
          onClick={() => onViewDetails(certificate)}
        />
        <Button
          label={t('myCertifications.card.addCertificate')}
          variant="contained"
          color="primary"
          disableRipple
          styleClass={classes.requiredButton}
          testId={`required-certificate-add-button-${certificate.id}`}
          onClick={() => onAddCertificate(certificate)}
        />
      </div>
    </div>
  );
};
